import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

function showAddToCartNotification() {
  const existingToast = document.querySelector('.cart-toast-notification');
  if (existingToast) {
    existingToast.remove();
  }

  const toast = document.createElement('div');
  toast.className = 'cart-toast-notification';

  const icon = document.createElement('span');
  icon.className = 'cart-toast-icon';
  icon.textContent = '✓';

  const text = document.createElement('span');
  text.textContent = 'Added to cart! 🛒';

  toast.appendChild(icon);
  toast.appendChild(text);
  document.body.appendChild(toast);

  setTimeout(() => {
    toast.classList.add('hide');
    toast.addEventListener('animationend', () => {
      toast.remove();
    });
  }, 2500);
}

// Bundle Validation Engine
(function() {
  const BUNDLE_SIZE = 4;
  const DISCOUNT_CODE = 'BUNDLE20';
  const DISCOUNT_PERCENTAGE = 0.15;

  function enforceBundles() {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
    .then(res => res.json())
    .then(cart => {
      const bundleCounts = {};
      cart.items.forEach(item => {
        if (item.properties && item.properties['_bundle_id']) {
          const bId = item.properties['_bundle_id'];
          bundleCounts[bId] = (bundleCounts[bId] || 0) + item.quantity;
        }
      });

      const validBundles = new Set();
      for (const [bId, count] of Object.entries(bundleCounts)) {
        if (count >= BUNDLE_SIZE) {
          validBundles.add(bId);
        }
      }

      let hasAnyValidBundle = validBundles.size > 0;

      let currentlyHasBundle = window._lastBundleState;
      if (currentlyHasBundle !== undefined && hasAnyValidBundle !== currentlyHasBundle) {
          window._lastBundleState = hasAnyValidBundle;
          if (hasAnyValidBundle) {
             fetch('/discount/' + DISCOUNT_CODE).catch(() => {});
             fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ discount: DISCOUNT_CODE, attributes: { 'discount': DISCOUNT_CODE, 'discount_code': DISCOUNT_CODE } })
             });
          } else {
             fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ discount: '', attributes: { 'discount': '', 'discount_code': '' } })
             });
          }
      } else {
          window._lastBundleState = hasAnyValidBundle;
      }
      
      if (hasAnyValidBundle) {
        window.merchantInfo = window.merchantInfo || {};
        window.merchantInfo.discountCode = DISCOUNT_CODE;
      } else {
        if (window.merchantInfo && window.merchantInfo.discountCode === DISCOUNT_CODE) {
          delete window.merchantInfo.discountCode;
        }
      }

      cart.items.forEach(item => {
        const itemEls = document.querySelectorAll(`.mc-item[data-key="${item.key}"], .cart-item[data-key="${item.key}"]`);
        itemEls.forEach(itemEl => {
          const priceContainer = itemEl.querySelector('.mc-item-price, .cart-item__price-wrapper');
          if (!priceContainer) return;

          const bId = item.properties ? item.properties['_bundle_id'] : null;

          if (bId && validBundles.has(bId)) {
            if (!priceContainer.hasAttribute('data-bundle-injected')) {
              priceContainer.setAttribute('data-original-html', priceContainer.innerHTML);
              priceContainer.setAttribute('data-bundle-injected', 'true');
              
              const priceCents = item.original_line_price || item.price;
              const discountedCents = priceCents * (1 - DISCOUNT_PERCENTAGE);
              
              const formatter = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 });
              // Store's own price format when the theme exposes it
              const money = (cents) =>
                typeof Theme !== 'undefined' && Theme.formatMoney
                  ? Theme.formatMoney(cents)
                  : 'Rs. ' + formatter.format(cents / 100);
              const newPriceStr = money(discountedCents);
              const oldPriceStr = money(priceCents);

              priceContainer.innerHTML = `
                <span style="text-decoration: line-through; color: #94a3b8; font-size: 0.9em; margin-right: 4px;">${oldPriceStr}</span>
                <span style="color: #e11d48; font-weight: bold;">${newPriceStr}</span>
                <div style="font-size: 10px; color: #e11d48; background: #ffe4e6; padding: 2px 6px; border-radius: 4px; display: inline-block; margin-top: 4px;">Bundle Savings</div>
              `;
            }
          } else {
            if (priceContainer.hasAttribute('data-bundle-injected')) {
              priceContainer.innerHTML = priceContainer.getAttribute('data-original-html');
              priceContainer.removeAttribute('data-bundle-injected');
              priceContainer.removeAttribute('data-original-html');
            }
          }
        });
      });

      const checkoutForms = document.querySelectorAll('form[action="/cart"], form[action="/checkout"]');
      checkoutForms.forEach(form => {
        let input = form.querySelector('input[name="discount"]');
        if (hasAnyValidBundle) {
          if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'discount';
            form.appendChild(input);
          }
          input.value = DISCOUNT_CODE;
        } else {
          if (input) input.remove();
        }
      });
      
      const checkoutButtons = document.querySelectorAll('button[name="checkout"], a[href="/checkout"], a[href*="/checkout?"]');
      checkoutButtons.forEach(btn => {
        if (btn.tagName === 'A') {
          let url = new URL(btn.href, window.location.origin);
          if (hasAnyValidBundle) {
            url.searchParams.set('discount', DISCOUNT_CODE);
          } else {
            url.searchParams.delete('discount');
          }
          btn.href = url.toString();
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enforceBundles);
  } else {
    enforceBundles();
  }

  document.addEventListener('cart:update', enforceBundles);
  document.addEventListener('cart:updated', enforceBundles);

  let bundleValidateTimeout;
  const observer = new MutationObserver((mutations) => {
    let shouldRun = false;
    for (const m of mutations) {
      if (m.addedNodes.length) {
        for (let i=0; i<m.addedNodes.length; i++) {
          const n = m.addedNodes[i];
          if (n.nodeType === 1 && (
              n.classList.contains('mc-item') || 
              n.classList.contains('cart-drawer__content') ||
              (n.querySelector && n.querySelector('.mc-item'))
          )) {
            shouldRun = true;
            break;
          }
        }
      }
      if (shouldRun) break;
    }
    if (shouldRun) {
      clearTimeout(bundleValidateTimeout);
      bundleValidateTimeout = setTimeout(enforceBundles, 100);
    }
  });
  
  observer.observe(document.body, { childList: true, subtree: true });
})();

/**
 * Click-and-drag horizontal scrolling for the "Frequently Bought Together"
 * row in the cart drawer.
 *
 * Touch and pen already pan the row natively (overflow-x + touch-action), so
 * this only takes over for a mouse, where there is otherwise no way to slide
 * the row short of grabbing the scrollbar.
 *
 * Listeners are bound once at the document level rather than on the row
 * itself: the drawer's contents are re-injected via innerHTML on every cart
 * update, which would discard any element-bound handler.
 */
(function () {
  if (window.__mcCrossDragBound) return;
  window.__mcCrossDragBound = true;

  // How far the pointer must travel before a press counts as a drag rather
  // than a click. Without it, the tiny movement during an ordinary click
  // would suppress the card's navigation.
  const DRAG_THRESHOLD = 5;

  let row = null;
  let startX = 0;
  let startScrollLeft = 0;
  let isDragging = false;

  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (!(event.target instanceof Element)) return;

    const target = event.target.closest('.mc-cross-row');
    if (!target) return;

    row = target;
    startX = event.clientX;
    startScrollLeft = target.scrollLeft;
    isDragging = false;
  });

  document.addEventListener('pointermove', (event) => {
    if (!row) return;

    const distance = event.clientX - startX;
    if (!isDragging) {
      if (Math.abs(distance) < DRAG_THRESHOLD) return;
      isDragging = true;
      row.classList.add('mc-cross-row--dragging');
    }

    row.scrollLeft = startScrollLeft - distance;
    // Stops the drag from selecting the card text as it passes over it.
    event.preventDefault();
  });

  function endDrag() {
    const dragged = row;
    const wasDragging = isDragging;

    row = null;
    isDragging = false;

    if (!dragged) return;
    dragged.classList.remove('mc-cross-row--dragging');
    if (!wasDragging) return;

    // A drag ends with a click event on whatever card sits under the cursor,
    // which would navigate to that product. Swallow that one click during the
    // capture phase, before it reaches the card's own handler.
    const swallowClick = (clickEvent) => {
      clickEvent.stopPropagation();
      clickEvent.preventDefault();
    };
    dragged.addEventListener('click', swallowClick, true);
    setTimeout(() => dragged.removeEventListener('click', swallowClick, true), 0);
  }

  document.addEventListener('pointerup', endDrag);
  document.addEventListener('pointercancel', endDrag);
})();
